import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { WebSocketContext } from './WebSocketContextUtil';
import {
  WebSocketEventType,
  ClientMessage,
  isWebSocketMessage,
  EventHandler,
  MessageHandler,
  PingMessage,
  RawMessageHandler,
  SubscriptionScope,
} from '../types/websocket';
import { config } from '../config';
import { withAccount } from '../lib/activeAccount';
import { isSessionExpired, probeSession } from '../lib/sessionExpiry';
import { accountSuspensionState } from '../lib/accountSuspension';
import { useAccountSuspension } from '../hooks/useAccountSuspension';

/**
 * How often an open socket says it's alive. A proxy drops a WebSocket that
 * carries nothing for a while — Cloudflare's limit is reported at 100s — and that
 * ends every terminal session open on it. Hidden tabs may run timers only once a
 * minute, which is still inside that.
 */
const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Enhanced WebSocket Provider with robust features:
 * - Event notification pattern (lightweight notifications + REST fetch)
 * - Handler registration system for event-specific callbacks
 * - sendMessage capability for subscriptions
 * - Automatic reconnection with exponential backoff
 * - React Query cache invalidation integration
 */
export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { suspended } = useAccountSuspension();
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Store event handlers - using refs to avoid recreating WebSocket on handler changes
  const eventHandlersRef = useRef<Map<WebSocketEventType, Set<EventHandler>>>(new Map());
  const globalHandlersRef = useRef<Set<MessageHandler>>(new Set());
  const rawHandlersRef = useRef<Set<RawMessageHandler>>(new Set());

  // Store active subscriptions
  const subscriptionsRef = useRef<Set<string>>(new Set());

  // Use refs for reconnection to avoid triggering useEffect
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const currentOrgIdRef = useRef<string | null>(null);
  const isUnmountingRef = useRef<boolean>(false);
  // The reconnect timer calls through this, not `connectWebSocket` itself: a callback
  // cannot reference itself from inside its own definition.
  const connectRef = useRef<() => void>(() => undefined);

  /**
   * Register a handler for a specific event type
   * Returns an unsubscribe function
   */
  const addEventListener = useCallback(
    <T = unknown,>(eventType: WebSocketEventType, handler: EventHandler<T>): (() => void) => {
      if (!eventHandlersRef.current.has(eventType)) {
        eventHandlersRef.current.set(eventType, new Set());
      }

      const handlers = eventHandlersRef.current.get(eventType);
      // Handler set is guaranteed to exist after the check above
      if (!handlers) return () => {};

      handlers.add(handler as EventHandler);

      // Return unsubscribe function
      return () => {
        handlers.delete(handler as EventHandler);
        if (handlers.size === 0) {
          eventHandlersRef.current.delete(eventType);
        }
      };
    },
    []
  );

  /**
   * Register a global handler that receives all messages
   * Returns an unsubscribe function
   */
  const addGlobalListener = useCallback((handler: MessageHandler): (() => void) => {
    globalHandlersRef.current.add(handler);

    return () => {
      globalHandlersRef.current.delete(handler);
    };
  }, []);

  /**
   * Register a raw message handler for non-standard messages (e.g., proxy data)
   * Returns an unsubscribe function
   */
  const addRawListener = useCallback((handler: RawMessageHandler): (() => void) => {
    rawHandlersRef.current.add(handler);
    return () => {
      rawHandlersRef.current.delete(handler);
    };
  }, []);

  /**
   * Send a raw JSON message to the WebSocket server
   */
  const sendRaw = useCallback(
    (message: Record<string, unknown>) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(message));
      } else {
        console.warn('WebSocket is not open. Cannot send raw message:', message);
      }
    },
    [ws]
  );

  /**
   * Send a message to the WebSocket server
   */
  const sendMessage = useCallback(
    (message: ClientMessage) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(message));
      } else {
        console.warn('WebSocket is not open. Cannot send message:', message);
      }
    },
    [ws]
  );

  /**
   * Subscribe to events for a specific scope (region, division, etc.)
   */
  const subscribe = useCallback(
    (scope: SubscriptionScope) => {
      const scopeKey = JSON.stringify(scope);

      if (subscriptionsRef.current.has(scopeKey)) {
        console.log('Already subscribed to:', scope);
        return;
      }

      subscriptionsRef.current.add(scopeKey);
      sendMessage({ type: 'subscribe', scope });
      console.log('Subscribed to:', scope);
    },
    [sendMessage]
  );

  /**
   * Unsubscribe from events for a specific scope
   */
  const unsubscribe = useCallback(
    (scope: SubscriptionScope) => {
      const scopeKey = JSON.stringify(scope);

      if (!subscriptionsRef.current.has(scopeKey)) {
        console.log('Not subscribed to:', scope);
        return;
      }

      subscriptionsRef.current.delete(scopeKey);
      sendMessage({ type: 'unsubscribe', scope });
      console.log('Unsubscribed from:', scope);
    },
    [sendMessage]
  );

  /**
   * Handle incoming WebSocket messages
   * Dispatches to registered event handlers
   */
  const handleMessage = useCallback((event: MessageEvent<string>) => {
    try {
      const data: unknown = JSON.parse(event.data);

      // Check raw handlers first (for proxy messages, etc.)
      for (const handler of rawHandlersRef.current) {
        try {
          if (handler(data)) {
            return; // Message was handled by a raw listener, skip normal processing
          }
        } catch (error) {
          console.error('Error in raw WebSocket handler:', error);
        }
      }

      if (!isWebSocketMessage(data)) {
        console.warn('Received non-WebSocket message:', data);
        return;
      }

      const message = data;

      // The answer to the heartbeat: nothing to hand on.
      if (message.eventType === WebSocketEventType.PONG) {
        return;
      }

      // Call global handlers
      globalHandlersRef.current.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in global WebSocket handler:', error);
        }
      });

      // Call event-specific handlers
      const handlers = eventHandlersRef.current.get(message.eventType);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(message.payload, message);
          } catch (error) {
            console.error(`Error in WebSocket handler for ${message.eventType}:`, error);
          }
        });
      }

      // Log unhandled events (for debugging)
      if (!handlers || handlers.size === 0) {
        console.log('Unhandled WebSocket event:', message.eventType, message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }, []);

  /**
   * Establish WebSocket connection
   */
  const connectWebSocket = useCallback(() => {
    // The socket is scoped to one organization, and it must be the one the
    // active context is in. `user.organizationId` is only the user's first org
    // by id, so for anyone in two orgs it can name one they aren't working in —
    // every device lookup over the socket (terminal sessions) then misses.
    // Switching context refetches `user`, and a changed org reconnects below.
    const orgId = user?.activeContext?.organizationId ?? user?.organizationId;
    if (!orgId) {
      console.log('Organization ID not available, skipping WebSocket connection.');
      return;
    }

    // If we're already connected to the same organization, don't reconnect
    if (
      currentOrgIdRef.current === orgId &&
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log('Already connected to organization:', orgId);
      return;
    }

    // Close existing connection if organization changed or connection is closed
    if (wsRef.current) {
      console.log('Closing existing WebSocket connection (org changed or connection dead)...');
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // Clear any pending reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Update current org ID
    currentOrgIdRef.current = orgId;

    // Note: JWT token is automatically sent via HTTP-only cookie. A socket can't send the
    // account header, so the tab's account rides on the URL (docs/MultiAccountSessions.md §4).
    const wsUrl = withAccount(`${config.websocketApi}?orgId=${orgId}`);
    console.log('Connecting to WebSocket:', wsUrl);
    const newWs = new WebSocket(wsUrl);

    let everOpened = false;
    let heartbeat: number | undefined;

    newWs.onopen = () => {
      console.log('WebSocket connected!');
      everOpened = true;
      heartbeat = setInterval(() => {
        if (newWs.readyState === WebSocket.OPEN) {
          const ping: PingMessage = { type: 'ping', timestamp: new Date().toISOString() };
          newWs.send(JSON.stringify(ping));
        }
      }, HEARTBEAT_INTERVAL_MS) as unknown as number;
      setIsConnected(true);
      reconnectAttemptsRef.current = 0; // Reset attempts on successful connection

      // Resubscribe to all active subscriptions
      subscriptionsRef.current.forEach((scopeKey) => {
        const scope = JSON.parse(scopeKey) as SubscriptionScope;
        newWs.send(JSON.stringify({ type: 'subscribe', scope }));
      });
    };

    newWs.onmessage = handleMessage;

    newWs.onclose = (event) => {
      clearInterval(heartbeat);

      // A socket we replaced (org changed) closes after its successor is up.
      // Its close says nothing about the live connection, and a reconnect
      // scheduled from here would run this closure's stale org.
      if (wsRef.current !== newWs) {
        return;
      }

      console.log('WebSocket disconnected:', event.code, event.reason);
      setIsConnected(false);

      // Don't reconnect if component is unmounting or user logged out
      if (isUnmountingRef.current || !currentOrgIdRef.current) {
        console.log('Component unmounting or user logged out, not reconnecting');
        return;
      }

      // Don't reconnect if connection was intentionally closed (normal closure)
      if (event.code === 1000) {
        console.log('WebSocket closed normally, not reconnecting');
        return;
      }

      // The re-auth dialog is already up; reconnecting would just 401 again.
      // It reconnects on its own once `user` is refreshed after sign-in.
      if (isSessionExpired()) {
        console.log('Session expired, not reconnecting WebSocket');
        return;
      }

      // Likewise for a suspended account: the upgrade is refused with a 402
      // until the bill is paid, and the effect above reconnects when it is.
      if (accountSuspensionState().suspended) {
        console.log('Account suspended, not reconnecting WebSocket');
        return;
      }

      // A socket that closes without ever opening means the upgrade itself was
      // refused. The backend answers 401 there for a lapsed cookie, which the
      // browser reports as an opaque 1006 — so ask the API directly rather than
      // retrying a handshake that can never succeed. This is what turns an idle
      // tab's dead session into a visible prompt instead of a silent 30s retry
      // loop against a 401.
      if (!everOpened) {
        void probeSession();
      }

      // Implement reconnection logic with exponential backoff
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
      console.log(`WebSocket reconnection attempt ${reconnectAttemptsRef.current.toString()}, waiting ${(delay / 1000).toString()} seconds...`);

      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('Attempting WebSocket reconnection now...');
        connectRef.current();
      }, delay) as unknown as number;
    };

    newWs.onerror = (error) => {
      console.error('WebSocket error:', error);
      // Don't close here - let onclose handle reconnection
    };

    wsRef.current = newWs;
    setWs(newWs);
  }, [user, handleMessage]);

  useEffect(() => {
    connectRef.current = connectWebSocket;
  }, [connectWebSocket]);

  // Logged out, or suspended: no socket. The effect below closes it; the exposed state
  // is cleared here, during render, on entering that state. It must be cleared rather
  // than left to the socket's close handler, which ignores a socket no longer current.
  const tornDown = !isLoading && (!user || suspended);
  const [wasTornDown, setWasTornDown] = useState(false);
  if (tornDown !== wasTornDown) {
    setWasTornDown(tornDown);
    if (tornDown) {
      setWs(null);
      setIsConnected(false);
    }
  }

  /**
   * Effect: Connect/disconnect WebSocket based on auth state
   * Only runs when user or isLoading changes, NOT when ws changes.
   *
   * No cleanup here: a new `user` (every context switch) must not drop the
   * socket. `connectWebSocket` replaces it only when the organization changed
   * or it has died; unmount is handled by the effect below.
   */
  useEffect(() => {
    isUnmountingRef.current = false;

    if (!isLoading && user && !suspended) {
      connectWebSocket();
    } else if (!isLoading && (!user || suspended)) {
      // User logged out, or the account was suspended — the backend refuses the
      // upgrade with a 402 in that case (the socket is gated separately because
      // it sits outside AuthLayer), so tear down rather than retry. Clearing
      // `suspended` re-runs this effect and reconnects.
      isUnmountingRef.current = true;
      currentOrgIdRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
      subscriptionsRef.current.clear();
      reconnectAttemptsRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, suspended]);

  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
    };
  }, []);

  const contextValue = {
    isConnected,
    sendMessage,
    subscribe,
    unsubscribe,
    addEventListener,
    addGlobalListener,
    addRawListener,
    sendRaw,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
